/**
 * Polygon の辺法線方向への **等距離 offset curve** を生成する純粋モジュール
 * （happa v1.1 Phase C.1 Step 2 追加）。
 *
 * 背景:
 * `polygon.ts#offsetPolygonRadial` は重心基準の放射拡大であり、辺法線方向の
 * 等距離オフセットではない。そのため鋭角頂点で所望のマージンが確保されず、
 * innerPoly の角付近で周囲テキストが innerPoly に重なる事故が発生していた
 * （ユーザ診断: 「シルエットを太らせた状態ではなく、滑らかな曲線として
 * テキストマージンを取るべき」）。
 *
 * 本モジュールは真の offset curve（Minkowski sum with disk の近似）を
 * 実装する独立ユーティリティ。`offsetPolygonRadial` は phantom ring
 * 構築など既存の経路で使い続け、テキストマージン専用に本関数を使用する
 * 切り分け。
 *
 * アルゴリズム（2D 平面・凸/凹混在対応）:
 * 1. 各辺 `(v[i], v[i+1])` を **外側法線方向** に `distance` だけ平行移動する。
 * 2. 隣接する 2 本のオフセット辺を、元頂点 `v[i]` 周りで繋ぐ:
 *    - 凸頂点（外側に曲がる）: 半径 `distance` の円弧で連結（disk の境界）
 *    - 凹頂点（内側に曲がる）: 2 本のオフセット辺の直線交点で連結
 *    - 極端な凹（miter が伸びすぎ）: miter limit で clamp（二等分線上で切断）
 *
 * **巻き向き**: 入力 polygon は **視覚 CW**（Y-down 画面で時計回り）前提。
 * これは `polygon.ts#generateInitialPolygon` の出力と一致し、本プロジェクトの
 * 全 polygon で保たれている（`signedArea > 0` になる）。視覚 CCW 入力は
 * 本関数の挙動が **未定義** であり、呼び出し側で巻き向きを揃えること。
 *
 * **符号規約**（視覚 CW = Y-down で clockwise、以下同）:
 * - 単位接線 `t = (tx, ty)` の **外側法線** は `n = (ty, -tx)`。
 *   直感: t=(1,0) の辺の outer normal は (0, -1)、つまり Y-down 画面で「上」。
 *   視覚 CW 多角形の外側は、進行方向から見て右側（Y-down で数学的には左）。
 * - 頂点の凹凸判定は隣接辺の外積 `cross(e_{i-1}, e_i)` で行う:
 *   - `cross > 0` → **凸**（外側に折れる、円弧補間が必要）
 *   - `cross < 0` → **凹**（内側に折れる、交点 or miter clamp）
 *   - `cross ≈ 0` → 直線近傍（両端点をそのまま採用）
 *
 * 設計参照:
 * - `docs/happa-designtool-v1.1-spec.md` §3.1 / §5.4.1（text margin）
 * - ユーザ診断レポート Phase C.1 Step 2（offset curve 方針確定）
 *
 * 依存: `./types` の `Vec2` のみ。DOM / Canvas / RNG 参照なし。
 */

import type { Vec2 } from './types';

/**
 * 凸頂点の円弧 join におけるデフォルト分割数。
 *
 * 6 分割は外角が最大（鋭角 ≒ π）のケースで約 30° 刻みとなり、
 * テキスト配置用途での視覚的な滑らかさとして過不足ない値。`arcSteps` 引数で
 * 呼び出し側から上書き可能。
 */
const DEFAULT_ARC_STEPS = 6;

/**
 * 退化判定の閾値（辺長 / cross 絶対値）。
 *
 * - 辺の長さがこの値未満 → 退化辺として skip
 * - 隣接辺の cross 絶対値がこの値未満 → 直線近傍として「交点無し」扱い
 *
 * 1e-6 は px 空間での実質ゼロ判定として十分（innerShort = 100+ px スケール）。
 */
const DEGEN_EPS = 1e-6;

/**
 * miter join の clamp 倍率。
 *
 * 凹頂点で 2 本のオフセット辺の交点までの距離が `distance * MITER_LIMIT` を
 * 超える場合、二等分線上で `distance * MITER_LIMIT` の位置まで切り詰める。
 * 2.0 は一般的な CAD/グラフィック系のデフォルトで、鋭角 60° 前後で clamp が
 * 効き始める感覚値。
 */
const MITER_LIMIT = 2.0;

/**
 * Polygon を辺法線方向に等距離オフセットする（Minkowski sum with disk の近似）。
 *
 * 凸頂点は半径 `distance` の円弧で接続し、凹頂点はオフセット辺の直線交点で
 * 接続する。ユーザ診断「シルエットを太らせた状態ではなく、滑らかな曲線として
 * テキストマージンを取るべき」（happa v1.1 Phase C.1 Step 2）に対応。
 *
 * 本関数は **視覚 CW 巻き**（`polygon.ts#generateInitialPolygon` の出力と同じ、
 * Y-down 画面で時計回り）を前提とする。CCW 入力時の挙動は未定義。`distance`
 * が `0` 以下の場合は負 offset（内側収縮）となるが本実装ではサポートせず、
 * 入力の shallow copy を返す（負 offset は自己交差検出が必要で範囲外）。
 *
 * @param polygon 元の閉ポリゴン（末尾頂点が先頭と一致しない前提、視覚 CW）。
 * @param distance 法線方向の平行移動距離（px、正のみサポート）。
 * @param arcSteps 凸頂点の円弧 join の分割数（default 6）。
 * @returns オフセット後のポリゴン。頂点数は元より増える（凸では arcSteps 個、
 *   凹では 1 個で置換）。元 polygon は変更されない。
 */
export function offsetPolygonCurve(
  polygon: Vec2[],
  distance: number,
  arcSteps: number = DEFAULT_ARC_STEPS,
): Vec2[] {
  // 退化入力: 3 頂点未満は面を成さず、負 offset は未サポート。両方 shallow copy。
  if (polygon.length < 3) return polygon.slice();
  if (distance <= 0) return polygon.slice();

  // Step 1: 各辺を外側法線方向に offset。退化辺 (|e| < EPS) は null で skip。
  const offsetEdges = buildOffsetEdges(polygon, distance);

  // 有効辺が 2 本未満になった場合は join のしようがない。fallback として原値返却。
  const validCount = offsetEdges.reduce((n, e) => n + (e ? 1 : 0), 0);
  if (validCount < 2) return polygon.slice();

  // Step 2: 元頂点 v[i] ごとに「前の offset 辺の終点 p1_{i-1}」と
  // 「次の offset 辺の始点 p0_i」を join する。
  return joinOffsetEdges(polygon, offsetEdges, distance, arcSteps);
}

// ─────────────────────────────────────────────────────────────────────────────
// Private helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 各辺のオフセット端点ペアを生成する（辺が退化していたら null）。
 *
 * 戻り値の配列長は `polygon.length` と等しく、インデックス `i` が辺
 * `(polygon[i], polygon[i+1])` に対応する。
 */
function buildOffsetEdges(polygon: Vec2[], distance: number): (readonly [Vec2, Vec2] | null)[] {
  const n = polygon.length;
  const edges: (readonly [Vec2, Vec2] | null)[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % n];
    // polygon.length >= 3 なので a / b は必ず存在するが TS の narrowing のため明示。
    if (!a || !b) {
      edges[i] = null;
      continue;
    }
    edges[i] = offsetEdge(a, b, distance);
  }
  return edges;
}

/**
 * 1 本の辺 `(a, b)` を外法線方向に `distance` だけ平行移動する。
 *
 * 外法線は視覚 CW 巻きにおいて `n = (ty, -tx)`（接線 `t = (b-a)/|b-a|`）。
 * 辺長が `DEGEN_EPS` 未満なら null（呼び出し側で skip）。
 */
function offsetEdge(a: Vec2, b: Vec2, distance: number): readonly [Vec2, Vec2] | null {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = Math.hypot(dx, dy);
  if (len < DEGEN_EPS) return null;
  // 外法線: 視覚 CW 多角形の外側は「進行方向から見て右（Y-down で数学的には左）」。
  // 接線 t=(tx,ty) に対し外法線 n=(ty,-tx) が成立（docstring 冒頭参照）。
  const nx = dy / len;
  const ny = -dx / len;
  const ox = nx * distance;
  const oy = ny * distance;
  const p0: Vec2 = [a[0] + ox, a[1] + oy];
  const p1: Vec2 = [b[0] + ox, b[1] + oy];
  return [p0, p1];
}

/**
 * 各元頂点で前後の offset 辺を繋ぐ（凸→円弧 / 凹→交点 or miter clamp）。
 *
 * ループ内で出力配列に push していくため、凸頂点 1 個で `arcSteps + 1` 点、
 * 凹頂点 1 個で 1 点、直線近傍で 2 点（前後の端点を両方）を追加する。
 */
function joinOffsetEdges(
  polygon: Vec2[],
  offsetEdges: (readonly [Vec2, Vec2] | null)[],
  distance: number,
  arcSteps: number,
): Vec2[] {
  const n = polygon.length;
  const out: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    // 元頂点 v[i] に入る辺 = edge[i-1]、出る辺 = edge[i]。
    const prevEdge = findPrevValidEdge(offsetEdges, i);
    const nextEdge = offsetEdges[i];
    const v = polygon[i];
    if (!v) continue;
    if (!prevEdge || !nextEdge) {
      // 退化辺が隣接する場合: 有効な方の端点だけを採用して join をスキップ。
      // out が過度に途切れるのを防ぐ最小限の保険。
      if (prevEdge) out.push(prevEdge[1]);
      if (nextEdge) out.push(nextEdge[0]);
      continue;
    }
    appendJoin(out, polygon, i, prevEdge, nextEdge, v, distance, arcSteps);
  }
  return out;
}

/**
 * `offsetEdges[i-1]` を起点に、循環的に有効な（null でない）1 本前の辺を探す。
 *
 * 退化辺が連続する場合に備えた探索だが、通常は `i-1` の 1 本で即ヒットする。
 * 全辺が null なら呼び出し側で既に早期 return 済み（`validCount < 2`）のため
 * 無限ループにはならないが、念のため 1 周したら null を返す。
 */
function findPrevValidEdge(
  edges: (readonly [Vec2, Vec2] | null)[],
  i: number,
): readonly [Vec2, Vec2] | null {
  const n = edges.length;
  for (let step = 1; step <= n; step++) {
    const idx = (i - step + n) % n;
    const e = edges[idx];
    if (e) return e;
  }
  return null;
}

/**
 * 1 頂点分の join を `out` に push する（凸/凹/直線の分岐）。
 *
 * 凹凸判定は「元頂点 v[i] での隣接辺外積」符号で行う:
 * - `cross > 0` → 凸（外側に曲がる）→ 円弧 join
 * - `cross < 0` → 凹（内側に曲がる）→ 直線交点 join、miter 超過で clamp
 * - `cross ≈ 0` → 直線近傍 → 前後 offset 辺の端点を両方採用（同一点になる）
 */
function appendJoin(
  out: Vec2[],
  polygon: Vec2[],
  i: number,
  prevEdge: readonly [Vec2, Vec2],
  nextEdge: readonly [Vec2, Vec2],
  v: Vec2,
  distance: number,
  arcSteps: number,
): void {
  const n = polygon.length;
  const vPrev = polygon[(i - 1 + n) % n];
  const vNext = polygon[(i + 1) % n];
  if (!vPrev || !vNext) return;
  const e1x = v[0] - vPrev[0];
  const e1y = v[1] - vPrev[1];
  const e2x = vNext[0] - v[0];
  const e2y = vNext[1] - v[1];
  const cross = e1x * e2y - e1y * e2x;
  const p1Prev = prevEdge[1];
  const p0Next = nextEdge[0];

  if (Math.abs(cross) < DEGEN_EPS) {
    // 直線近傍: 前後 offset 辺の端点は幾何的にほぼ同一点。
    // 両方 push しても重複頂点になるだけなので 1 点だけ採用（前端点を優先）。
    out.push(p1Prev);
    return;
  }
  if (cross > 0) {
    // 凸頂点: v を中心、半径 distance の円弧で p1Prev → p0Next を接続。
    const arc = arcJoin(v, p1Prev, p0Next, arcSteps);
    for (const p of arc) out.push(p);
    return;
  }
  // 凹頂点: offset 辺同士の直線交点。miter 超過時は二等分線 clamp。
  out.push(concaveJoin(v, prevEdge, nextEdge, distance));
}

/**
 * 凸頂点の円弧補間点列を生成する（v 中心、半径 `distance`）。
 *
 * `from` と `to` は両方とも v からの距離が `distance`（= offset 辺端点が
 * v 中心の円上にある）。slerp（signed shortest arc）で `steps + 1` 点補間。
 * `steps ≤ 0` の安全側として最低 1 分割（端点 2 点）は保証する。
 */
function arcJoin(center: Vec2, from: Vec2, to: Vec2, steps: number): Vec2[] {
  const ux = from[0] - center[0];
  const uy = from[1] - center[1];
  const vx = to[0] - center[0];
  const vy = to[1] - center[1];
  // signed sweep angle（最短側、[-π, π]）。cross / dot の atan2 で符号込みの角度。
  const cross = ux * vy - uy * vx;
  const dot = ux * vx + uy * vy;
  const sweep = Math.atan2(cross, dot);
  const segs = Math.max(1, steps);
  const out: Vec2[] = [];
  // t=0 / t=1 を含めて segs+1 点を生成。from / to を含むため呼び出し側で
  // 追加 push は不要（重複頂点を避ける）。
  for (let s = 0; s <= segs; s++) {
    const t = s / segs;
    const a = sweep * t;
    const cosA = Math.cos(a);
    const sinA = Math.sin(a);
    // v を中心として ux/uy を a だけ回転（標準 2D 回転行列、Y-down でも式は同じ）。
    out.push([center[0] + ux * cosA - uy * sinA, center[1] + ux * sinA + uy * cosA]);
  }
  return out;
}

/**
 * 凹頂点の join 点を計算する。
 *
 * 基本は前後 offset 辺の無限延長直線の交点。交点が元頂点 `v` から
 * `distance * MITER_LIMIT` を超えて離れている場合（miter が尖りすぎ）、
 * 二等分線上で miter limit 位置に clamp する。直線が平行で交点が無い
 * 場合も同様に miter clamp（二等分線方向のフォールバック）。
 */
function concaveJoin(
  v: Vec2,
  prevEdge: readonly [Vec2, Vec2],
  nextEdge: readonly [Vec2, Vec2],
  distance: number,
): Vec2 {
  const intersect = lineIntersection(prevEdge[0], prevEdge[1], nextEdge[0], nextEdge[1]);
  const miterCap = distance * MITER_LIMIT;
  const miterCap2 = miterCap * miterCap;
  if (intersect) {
    const dx = intersect[0] - v[0];
    const dy = intersect[1] - v[1];
    if (dx * dx + dy * dy <= miterCap2) return intersect;
  }
  // 二等分線 clamp: prev 辺と next 辺の offset 端点の中点方向を使う。
  // 平行 / 鋭角過ぎで交点が遠い場合の安全側。
  const mid: Vec2 = [
    (prevEdge[1][0] + nextEdge[0][0]) * 0.5,
    (prevEdge[1][1] + nextEdge[0][1]) * 0.5,
  ];
  const dx = mid[0] - v[0];
  const dy = mid[1] - v[1];
  const len = Math.hypot(dx, dy);
  if (len < DEGEN_EPS) return mid;
  const k = miterCap / len;
  return [v[0] + dx * k, v[1] + dy * k];
}

/**
 * 2 直線 `(a0→a1)` と `(b0→b1)` の交点を求める（平行なら null）。
 *
 * パラメトリック形 `a0 + t (a1-a0) = b0 + u (b1-b0)` を行列式で解く。
 * `t` / `u` の範囲に制限はなく、線分の内外を問わず無限延長の交点を返す
 * （offset 辺は凹頂点で必ず内向きに延長が必要なため）。
 */
function lineIntersection(a0: Vec2, a1: Vec2, b0: Vec2, b1: Vec2): Vec2 | null {
  const rx = a1[0] - a0[0];
  const ry = a1[1] - a0[1];
  const sx = b1[0] - b0[0];
  const sy = b1[1] - b0[1];
  const denom = rx * sy - ry * sx;
  if (Math.abs(denom) < DEGEN_EPS) return null;
  const dx = b0[0] - a0[0];
  const dy = b0[1] - a0[1];
  const t = (dx * sy - dy * sx) / denom;
  return [a0[0] + rx * t, a0[1] + ry * t];
}
